import { ICrypto } from '@/lib/types';
import { supabase } from './supabase';

interface CryptoRow {
    id: string;
    legacy_id: string | null;
    symbol: string;
    name: string;
    price: number;
    description: string | null;
    change_24h: number | null;
    expected_roi: number | null;
    risk_level: string | null;
    min_investment: number | null;
    market_cap: number | null;
    logo_url: string | null;
    created_at: string;
    metadata: any;
}

export const getCryptos = async (): Promise<ICrypto[]> => {
    try {
        const { data: cryptos, error } = await supabase
            .from('cryptos' as any)
            .select('*') as { data: CryptoRow[] | null, error: any };

        if (error) throw error;

        // Sort by marketCap descending
        const sorted = (cryptos || []).sort((a, b) => {
            const capA = Number(a?.market_cap) || 0;
            const capB = Number(b?.market_cap) || 0;
            return capB - capA;
        });

        return sorted.map(mapCryptoToDomain);
    } catch (error) {
        console.error('Failed to fetch cryptos:', error);
        return [];
    }
};

export const getSingleCrypto = async (params: { id: string }): Promise<ICrypto | null> => {
    try {
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(params.id);
        let query = supabase.from('cryptos' as any).select('*');
        if (isUuid) {
            query = query.eq('id', params.id);
        } else {
            query = query.eq('legacy_id', params.id);
        }

        const { data: crypto, error } = await query.maybeSingle() as { data: CryptoRow | null, error: any };

        if (error || !crypto) return null;
        return mapCryptoToDomain(crypto);
    } catch (error) {
        console.error('Failed to fetch single crypto:', error);
        return null;
    }
};

function mapCryptoToDomain(c: CryptoRow): ICrypto {
    const riskMap: Record<string, 'Low' | 'Medium' | 'High'> = {
        'low': 'Low',
        'medium': 'Medium',
        'high': 'High'
    };

    return {
        id: c.id,
        createdAt: c.created_at,
        symbol: c.symbol,
        name: c.name,
        price: Number(c.price) || 0,
        description: c.description || '',
        change24h: Number(c.change_24h) || 0,
        expectedROI: Number(c.expected_roi) || 0,
        riskLevel: (c.risk_level ? riskMap[c.risk_level.toLowerCase()] : 'unknown') as any || 'unknown',
        minInvestment: Number(c.min_investment) || 0,
        marketCap: Number(c.market_cap) || 0,
        logoUrl: c.logo_url
    };
}
